import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { UserService } from '../services/user.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { DatabaseService } from '../services/database.service';
import { GraphService } from '../services/graph.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AddRequestorDialogComponent } from '../add-requestor-dialog/add-requestor-dialog.component';
import { concat, forkJoin, Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ColorService } from '../services/color.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  /**
   * Compute layout for daily events so they stack vertically and only overlap horizontally when times intersect.
   * Uses a clustering + column assignment algorithm similar to common calendar implementations.
   */
  styleUrls: ['./dashboard.component.scss'],
  standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  /** Calendar day layout algorithm version (for traceability) */
  private static readonly CAL_LAYOUT_VERSION = 'sweep-1.0';
  private readonly monthCellVisibleLimit = 2;
  selectedAssignees: string[] = [];
  allAssigneesSelected = true;
  private assigneeStorageKey: string | null = null;
  currentUser: any = null;
  private colorUpdateSubscription: Subscription | null = null;

  calendarView = { day: false, week: false, month: true };
  calendarViewSelected: 'day' | 'week' | 'month' = 'month';

  months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];
  years: number[] = [];
  selectedYear = new Date().getFullYear();
  selectedMonth = new Date().getMonth();
  selectedWeekStartDate = new Date(); // Will be properly initialized in ngOnInit
  selectedDayDate = new Date(); // Track the current selected day
  allAdmins: any[] = [];

  get selectedMonthLabel() {
    return this.months[this.selectedMonth] + ', ' + this.selectedYear;
  }

  selectMonth(index: number) {
    this.selectedMonth = index;
    this.updateSelectedDates();
    this.loadEventsForCurrentMonth();
    this.updateUrlState();
  }

  selectYear(year: number) {
    this.selectedYear = year;
    this.updateSelectedDates();
    this.loadEventsForCurrentMonth();
    this.updateUrlState();
  }

  // Update selected dates when month/year changes
  updateSelectedDates() {
    // For daily view: Update selected day to today if we're in current month/year, otherwise first of month
    const today = new Date();
    if (this.selectedYear === today.getFullYear() && this.selectedMonth === today.getMonth()) {
      this.selectedDayDate = new Date(today);
    } else {
      this.selectedDayDate = new Date(this.selectedYear, this.selectedMonth, 1);
    }
    
    // For weekly view: Update selected week to contain today if we're in current month/year, otherwise first of month
    if (this.selectedYear === today.getFullYear() && this.selectedMonth === today.getMonth()) {
      this.selectedWeekStartDate = this.getWeekStartDate(today);
    } else {
      const firstOfMonth = new Date(this.selectedYear, this.selectedMonth, 1);
      this.selectedWeekStartDate = this.getWeekStartDate(firstOfMonth);
    }
  }

  private syncMonthYearWithSelectedDay(loadEvents: boolean = true): void {
    const dayMonth = this.selectedDayDate.getMonth();
    const dayYear = this.selectedDayDate.getFullYear();
    const hasChanged = dayMonth !== this.selectedMonth || dayYear !== this.selectedYear;

    if (!hasChanged) return;

    this.selectedMonth = dayMonth;
    this.selectedYear = dayYear;

    if (loadEvents) {
      this.loadEventsForCurrentMonth();
    }
  }

  // Navigation methods for daily view
  goToPreviousDay() {
    this.selectedDayDate = new Date(this.selectedDayDate);
    this.selectedDayDate.setDate(this.selectedDayDate.getDate() - 1);
    this.syncMonthYearWithSelectedDay();
    this.updateUrlState();
  }

  goToNextDay() {
    this.selectedDayDate = new Date(this.selectedDayDate);
    this.selectedDayDate.setDate(this.selectedDayDate.getDate() + 1);
    this.syncMonthYearWithSelectedDay();
    this.updateUrlState();
  }

  // Navigation methods for monthly view
  goToPreviousMonth() {
    if (this.selectedMonth === 0) {
      this.selectedMonth = 11;
      this.selectedYear--;
    } else {
      this.selectedMonth--;
    }
    this.updateSelectedDates();
    this.loadEventsForCurrentMonth();
    this.updateUrlState();
  }

  goToNextMonth() {
    if (this.selectedMonth === 11) {
      this.selectedMonth = 0;
      this.selectedYear++;
    } else {
      this.selectedMonth++;
    }
    this.updateSelectedDates();
    this.loadEventsForCurrentMonth();
    this.updateUrlState();
  }

  goToToday() {
    this.selectedDayDate = new Date();
    this.selectedWeekStartDate = this.getWeekStartDate(new Date());
    this.selectedYear = this.selectedDayDate.getFullYear();
    this.selectedMonth = this.selectedDayDate.getMonth();
    this.loadEventsForCurrentMonth();
    this.updateUrlState();
  }

  // Navigation methods for weekly view
  goToPreviousWeek() {
    this.selectedWeekStartDate = new Date(this.selectedWeekStartDate);
    this.selectedWeekStartDate.setDate(this.selectedWeekStartDate.getDate() - 7);
    // If this is differnt month, load events for that month
    if (this.selectedWeekStartDate.getMonth() !== this.selectedMonth || this.selectedWeekStartDate.getFullYear() !== this.selectedYear) {
      this.selectedMonth = this.selectedWeekStartDate.getMonth();
      this.selectedYear = this.selectedWeekStartDate.getFullYear();
      this.loadEventsForCurrentMonth();
    }
    this.updateUrlState();
  }

  goToNextWeek() {
    this.selectedWeekStartDate = new Date(this.selectedWeekStartDate);
    this.selectedWeekStartDate.setDate(this.selectedWeekStartDate.getDate() + 7);
    // If this is differnt month, load events for that month
    if (this.selectedWeekStartDate.getMonth() !== this.selectedMonth || this.selectedWeekStartDate.getFullYear() !== this.selectedYear) {
      this.selectedMonth = this.selectedWeekStartDate.getMonth();
      this.selectedYear = this.selectedWeekStartDate.getFullYear();
      this.loadEventsForCurrentMonth();
    }
    this.updateUrlState();
  }

  // Helper to get the Sunday of a given date's week
  getWeekStartDate(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    // Calculate days to subtract to get to Sunday (0)
    const diff = day;
    return new Date(d.setDate(d.getDate() - diff));
  }

  allEvents: any[] = [];
  holidayEvents: any[] = [];
  uniqueRequestors: string[] = [];
  requestorColors: { [key: string]: string } = {};
  
  // Debug properties
  get debugInfo(): string {
    return `Events: ${this.allEvents.length}, Requestors: ${this.uniqueRequestors.length}, Colors: ${Object.keys(this.requestorColors).length}`;
  }

  // Helper methods for template debugging
  get colorKeys(): string[] {
    return Object.keys(this.requestorColors);
  }

  get localStorageColors(): string {
    try {
      return localStorage.getItem('userColors') || '{}';
    } catch {
      return '{}';
    }
  }

  // TEMP TEST MODE: Disable predefined palette. Keep empty array to avoid errors where referenced.
  colorPalette: string[] = [];

  // Flag to prevent URL update loops during state restoration
  private isRestoringFromUrl = false;

  constructor(private _user: UserService, 
    private _db: DatabaseService,
    private router: Router, 
    private route: ActivatedRoute,
    private location: Location,
    private graphService: GraphService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private colorService: ColorService) { }

  // Register a color for a GMID and likely aliases (email variants)
  private registerColorAliases(gmid: string, color: string): void {
    if (!gmid || !color) return;
    const base = gmid.toLowerCase();
    this.requestorColors[base] = color;
    // Common email domains / forms seen in events
    const domains = ['@gm.com', '@global.gm.com'];
    domains.forEach(d => this.requestorColors[base + d] = color);
    // If the gmid already looks like an email, also store its local part
    if (base.includes('@')) {
      const local = base.split('@')[0];
      this.requestorColors[local] = color;
    }
  }

  private toPersonObject(person: any): any {
    if (!person) return null;
    if (typeof person === 'object') return person;
    const raw = String(person).trim();
    if (!raw) return null;
    if (raw.startsWith('{') && raw.endsWith('}')) {
      try {
        return JSON.parse(raw);
      } catch {
        // fall through
      }
    }
    return {
      email: raw.includes('@') ? raw : '',
      gmid: raw.includes('@') ? raw.split('@')[0] : raw,
      name: raw
    };
  }

  private getPersonEmail(person: any): string {
    const obj = this.toPersonObject(person);
    return (obj?.email || '').toLowerCase();
  }

  private getPersonGmid(person: any): string {
    const obj = this.toPersonObject(person);
    const gmid = obj?.gmid || (obj?.email ? obj.email.split('@')[0] : '');
    return (gmid || '').toLowerCase();
  }

  private getPersonName(person: any): string {
    const obj = this.toPersonObject(person);
    return (obj?.name || obj?.displayName || '').toLowerCase();
  }

  private getPersonDisplay(person: any): string {
    const obj = this.toPersonObject(person);
    if (!obj) return '';
    return obj.name || obj.displayName || obj.email || obj.gmid || '';
  }

  // Set role-based event filtering
  setRoleBasedFiltering(): void {
    if (!this.currentUser) return;
    const identity = this.currentUser.gmid || this.currentUser.userPrincipalName || this.currentUser.mail;
    if (!identity) return;
    const key = `calendar.assignees:${String(identity).toLowerCase()}`;
    if (key === this.assigneeStorageKey) return;
    this.assigneeStorageKey = key;
    this.allAssigneesSelected = true;
    this.selectedAssignees = [];
    try {
      const saved = JSON.parse(localStorage.getItem(key) || 'null');
      if (saved && typeof saved.all === 'boolean' && Array.isArray(saved.selected)
          && saved.selected.every((value: unknown) => typeof value === 'string')) {
        this.allAssigneesSelected = saved.all;
        this.selectedAssignees = saved.selected;
      }
    } catch {}
    if (this.allAssigneesSelected) {
      this.selectedAssignees = this.assigneeOptions;
    }
  }

  get assigneeOptions(): string[] {
    return ['unassigned', ...new Set<string>(this.allAdmins
      .map(admin => String(admin.email || '').trim().toLowerCase())
      .filter(email => !!email))];
  }

  get assigneeFilterValues(): string[] {
    return this.allAssigneesSelected ? ['__all__', ...this.selectedAssignees] : this.selectedAssignees;
  }

  // Debug method to get event creators summary
  getEventCreatorsSummary(): string {
    if (!this.allEvents || this.allEvents.length === 0) return 'No events';
    const creators = [...new Set(this.allEvents.map(ev => ev.creator))];
    return creators.join(', ');
  }

  // Debug method to check if user has any events
  getUserEventsSummary(): string {
    if (!this.currentUser || !this.allEvents) return 'No user or events';
    
    const userGmid = this.currentUser.gmid?.toLowerCase();
    const userEmail = this.currentUser.userPrincipalName || this.currentUser.mail;
    const userMail = this.currentUser.mail?.toLowerCase(); // Direct mail field
    
    
    
    
    const userEvents = this.allEvents.filter(event => {
      // Enhanced matching with email formats
      const requestorEmail = this.getPersonEmail(event.requestor);
      const requestorGmid = this.getPersonGmid(event.requestor);
      const eventLeaderEmail = this.getPersonEmail(event.event_leader);
      const eventLeaderGmid = this.getPersonGmid(event.event_leader);

      const isGmidMatch = event.creator === userGmid || 
                         requestorGmid === userGmid ||
                         eventLeaderGmid === userGmid;
      
      const isEmailMatch = userEmail && (
                          event.creator === userEmail.toLowerCase() ||
                          requestorEmail === userEmail.toLowerCase() ||
                          eventLeaderEmail === userEmail.toLowerCase());
      
      // Check direct mail field match - THIS IS THE KEY FIX
      const isMailMatch = userMail && (
                         event.creator === userMail ||
                         requestorEmail === userMail ||
                         eventLeaderEmail === userMail);
      
      // Also check if user email contains the gmid
      const isPartialMatch = userEmail && (
        userEmail.toLowerCase().includes(userGmid) ||
        requestorEmail.includes(userGmid) ||
        eventLeaderEmail.includes(userGmid) ||
        requestorGmid.includes(userGmid) ||
        eventLeaderGmid.includes(userGmid)
      );
      
      // // console.log(`Event ${event.title}: creator=${event.creator}, requestor=${event.requestor}, event_leader=${event.event_leader}, matches: gmid=${isGmidMatch}, email=${isEmailMatch}, mail=${isMailMatch}, partial=${isPartialMatch}`);
      
      return isGmidMatch || isEmailMatch || isMailMatch || isPartialMatch;
    });
    
    return `${userEvents.length} events found for user "${userGmid}" (email: ${userEmail}, mail: ${userMail})`;
  }

  ngOnInit(): void {
    // Refresh user permissions every time the calendar loads
    // This ensures viewer status is always up to date
    this._user.refreshCurrentUserPermissions();

    // Initialize years array with a range around the current year
    const currentYear = new Date().getFullYear();
    this.years = [];
    for (let year = currentYear - 2; year <= currentYear + 3; year++) {
      this.years.push(year);
    }
    
    // Properly initialize the week start date to the beginning of the current week
    this.selectedWeekStartDate = this.getWeekStartDate(new Date());
    
    // Subscribe to color updates from admin component
    this.colorUpdateSubscription = this.colorService.colorUpdated$.subscribe(
      ({ gmid, color }) => {
        // console.log(`🎨 Dashboard received color update for ${gmid}: ${color}`);
        // Update with aliases to catch creator variations (gmid vs email)
        this.registerColorAliases(gmid, color);
        
        // Force multiple change detection cycles to ensure UI updates
        this.cdr.detectChanges();
        
        // Additional change detection after a brief delay
        setTimeout(() => {
          this.cdr.detectChanges();
          // console.log('🔄 Triggered additional change detection for color update');
        }, 50);
        
        // Also mark for check to ensure all change detection strategies work
        setTimeout(() => {
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        }, 100);
      }
    );
    
    // Subscribe to user changes to implement role-based filtering
    this._user.user$.subscribe(user => {
      this.currentUser = user;
      if (this.currentUser) {
        // Set role-based event filtering
        this.setRoleBasedFiltering();
        
        // Restore calendar state from URL parameters
        this.restoreCalendarStateFromUrl();
        
        // Load requestors and events immediately - colors will load in parallel
        // this.loadAllRequestors();
        this.loadEventsForCurrentMonth();
      }
    });

    this._db.getAllAdmins().subscribe({
      next: (admins) => {
        this.allAdmins = admins.admins;
        if (this.allAssigneesSelected) this.selectedAssignees = this.assigneeOptions;
        console.log('All admins loaded:', this.allAdmins);
        this.loadCustomColors();
      },
      error: (error) => {
        console.error('Error loading admins:', error);
      }
    });
    
    // Check if the URL contains 'event-forms'
    // Navigation logic removed since we only show calendar view now
    
    // Initialize selected dates - start with today for daily/weekly views
    const today = new Date();
    this.selectedDayDate = new Date(today);
    this.selectedWeekStartDate = this.getWeekStartDate(today);
    this.updateSelectedDates();
  }

  // Load events from database
  loadEventsForCurrentMonth(): void {
    // Load events for current month, and month before and month after. This protects us from issues with weekly view crossing month boundaries
    let startRange = new Date(this.selectedYear, this.selectedMonth - 1, 1);
    let endRange = new Date(this.selectedYear, this.selectedMonth + 2, 0);
    // Format as 'YYYY-MM-DD' for API
    const startStr = startRange.toISOString().substring(0, 10);
    const endStr = endRange.toISOString().substring(0, 10);
    
    // Load both regular events and holiday events in parallel
    const databaseEvents$ = this._db.getEventsByDateRange(startStr, endStr);
    const holidayEvents$ = this._db.getHolidayEvents();
    
    forkJoin({
      events: databaseEvents$,
      holidays: holidayEvents$.pipe(catchError((err: any) => {
        console.warn('Failed to load holidays, continuing without them:', err);
        return of([]);
      }))
    }).subscribe({
      next: (results) => {
        // console.log('Holiday data received:', results.holidays);
        
        // Process Database events (handle both array and object responses)
        let databaseEvents = results.events;
        if (databaseEvents && typeof databaseEvents === 'object') {
          // Handle if API returns {events: [...]} or direct array
          databaseEvents = databaseEvents.events || databaseEvents;
        }
        
        const processedDatabaseEvents = (Array.isArray(databaseEvents) ? databaseEvents : [])
          .filter((ev: any) => {
            if (!ev?.start_date || !ev?.end_date) return false;

            const startDate = new Date(ev.start_date);
            const endDate = new Date(ev.end_date);
            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return false;

            const status = String(ev?.request_status || '').toLowerCase();
            return status !== "unsubmitted" && status !== "canceled";
          })
          .map((ev: any) => {
            const processedEvent = {
              ...ev,
              source: 'database',
              creator: String(this.getPersonDisplay(ev.requestor) || this.getPersonDisplay(ev.event_leader) || 'unknown').toLowerCase(),
              date: ev.start_date,
              title: ev.request_title || 'Untitled Event',
              subject: ev.request_title || 'Untitled Event', // For compatibility with existing template
              type: 'broadcast',
              event_id: ev.event_id // Ensure event_id is preserved
            };
            
            return processedEvent;
          });

        // Process Holiday events
        let holidayData = results.holidays;
        if (holidayData && typeof holidayData === 'object') {
          // Handle if API returns {holidays: [...]} or direct array
          holidayData = (holidayData as any).holidays || holidayData;
        }
        
        const processedHolidayEvents = (Array.isArray(holidayData) ? holidayData : [])
          .filter((holiday: any) => holiday && holiday.date) // Filter out invalid holidays
          .map((holiday: any) => {
            try {
              // Validate and parse the holiday date
              let dateStr = holiday.date;
              
              // Handle different date formats
              if (dateStr instanceof Date) {
                dateStr = dateStr.toISOString().substring(0, 10);
              } else if (typeof dateStr === 'string') {
                // Ensure it's in YYYY-MM-DD format
                if (dateStr.includes('T')) {
                  dateStr = dateStr.substring(0, 10);
                }
              }
              
              // Validate the date string format (YYYY-MM-DD)
              if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                console.warn('Invalid holiday date format:', holiday.date);
                return null;
              }
              
              // Create dates with proper validation
              const startDate = new Date(dateStr + 'T00:00:00.000');
              const endDate = new Date(dateStr + 'T23:59:59.999');
              
              // Validate the created dates
              if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                console.warn('Invalid holiday date:', dateStr);
                return null;
              }
              
              return {
                id: holiday.id,
                source: 'holiday',
                creator: 'system',
                date: dateStr,
                start_date: startDate.toISOString(),
                end_date: endDate.toISOString(),
                title: 'Holiday',
                subject: 'Holiday',
                type: 'holiday',
                request_type: 'Holiday/Vacation/Internal Testing',
                request_status: 'Completed',
                isHoliday: true
              };
            } catch (error) {
              console.error('Error processing holiday:', holiday, error);
              return null;
            }
          })
          .filter(holiday => holiday !== null); // Remove any null entries from failed processing

        // Store holidays separately for reference
        this.holidayEvents = processedHolidayEvents;

        // Combine regular events and holiday events
        this.allEvents = [...processedDatabaseEvents, ...processedHolidayEvents];
        
        // Trigger change detection to ensure events display
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading events:', error);
        console.error('Error details:', error.error);
        console.error('Error status:', error.status);
        
        // Set empty arrays if loading fails
        this.allEvents = [];
        this.holidayEvents = [];
      }
    });
  }

  setCalendarView(view: 'day' | 'week' | 'month') {
    this.calendarView = { day: false, week: false, month: false };
    this.calendarView[view] = true;
    this.calendarViewSelected = view;
    
    // When switching to daily or weekly view, set the dates to show today if in current month/year
    const today = new Date();
    const isCurrentMonth = this.selectedYear === today.getFullYear() && this.selectedMonth === today.getMonth();
    
    if (view === 'day' && isCurrentMonth) {
      this.selectedDayDate = new Date(today);
    } else if (view === 'week' && isCurrentMonth) {
      this.selectedWeekStartDate = this.getWeekStartDate(today);
    }
    
    // Update URL to maintain browser history
    this.updateUrlState();
  }

  /**
   * Format a date for URL parameters avoiding timezone issues
   */
  private formatDateForUrl(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Parse a date from URL parameters avoiding timezone issues
   */
  private parseDateFromUrl(dateString: string): Date | null {
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
    const day = parseInt(parts[2], 10);
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    
    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return null;
    
    return date;
  }

  /**
   * Update the browser URL with current calendar state
   */
  private updateUrlState(): void {
    // Don't update URL if we're currently restoring from URL
    if (this.isRestoringFromUrl) {
      return;
    }

    const queryParams: any = {
      view: this.calendarViewSelected,
      month: this.selectedMonth.toString(),
      year: this.selectedYear.toString()
    };

    // Add selected date for daily view
    if (this.calendarViewSelected === 'day' && this.selectedDayDate) {
      queryParams['selectedDate'] = this.formatDateForUrl(this.selectedDayDate);
    }

    // Add selected week start date for weekly view
    if (this.calendarViewSelected === 'week' && this.selectedWeekStartDate) {
      queryParams['selectedWeekStart'] = this.formatDateForUrl(this.selectedWeekStartDate);
    }

    // Update URL without triggering navigation
    this.location.replaceState(
      this.router.url.split('?')[0], 
      new URLSearchParams(queryParams).toString()
    );
  }

  /**
   * Restore calendar state from URL parameters
   */
  private restoreCalendarStateFromUrl(): void {
    this.route.queryParams.subscribe(params => {
      // Set flag to prevent URL updates during restoration
      this.isRestoringFromUrl = true;

      try {
        // Restore view type
        if (params['view'] && ['day', 'week', 'month'].includes(params['view'])) {
          const view = params['view'] as 'day' | 'week' | 'month';
          this.calendarViewSelected = view;
          this.calendarView = { day: false, week: false, month: false };
          this.calendarView[view] = true;
        }

        // Restore selected month and year
        if (params['month'] && params['year']) {
          const month = parseInt(params['month'], 10);
          const year = parseInt(params['year'], 10);
          
          if (month >= 0 && month <= 11 && year >= 1900 && year <= 2100) {
            this.selectedMonth = month;
            this.selectedYear = year;
          }
        }

        // Restore selected date for daily view
        if (params['selectedDate'] && this.calendarViewSelected === 'day') {
          const date = this.parseDateFromUrl(params['selectedDate']);
          if (date) {
            this.selectedDayDate = date;
            this.syncMonthYearWithSelectedDay();
          }
        }

        // Restore selected week start date for weekly view
        if (params['selectedWeekStart'] && this.calendarViewSelected === 'week') {
          const date = this.parseDateFromUrl(params['selectedWeekStart']);
          if (date) {
            this.selectedWeekStartDate = date;
          }
        }
      } finally {
        // Always reset the flag, even if there's an error
        this.isRestoringFromUrl = false;
      }
    });
  }

  // Method to handle day number clicks in monthly view
  onDayNumberClick(week: number, day: number): void {
    const dateStr = this.getDateForCell(week, day);
    if (!dateStr) return; // Don't do anything if it's not a valid date
    
    // Set the selected day date
    const selectedDate = new Date(this.selectedYear, this.selectedMonth, parseInt(dateStr));
    this.selectedDayDate = selectedDate;
    this.syncMonthYearWithSelectedDay();
    
    // Switch to daily view without calling setCalendarView to preserve the selected date
    this.calendarView = { day: true, week: false, month: false };
    this.calendarViewSelected = 'day';
    
    // Update URL to maintain browser history
    this.updateUrlState();
    
    // console.log(`Switched to daily view for: ${selectedDate.toDateString()}`);
  }

  // Method to handle day number clicks in weekly view
  onWeekDayClick(dayIndex: number): void {
    // Get the date for this day in the current week
    const selectedDate = this.getWeekDayDate(dayIndex);
    this.selectedDayDate = new Date(selectedDate);
    this.syncMonthYearWithSelectedDay();
    
    // Switch to daily view without calling setCalendarView to preserve the selected date
    this.calendarView = { day: true, week: false, month: false };
    this.calendarViewSelected = 'day';
    
    // Update URL to maintain browser history
    this.updateUrlState();
    
    // console.log(`Switched to daily view for: ${selectedDate.toDateString()}`);
  }

  getWeekViewDate(day: number): number | '' {
    // Find the first visible date in the current month view
    // Find the week containing today or the first of the month
    const isLeap = (this.selectedYear % 4 === 0 && (this.selectedYear % 100 !== 0 || this.selectedYear % 400 === 0));
    const monthDays = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const daysInMonth = monthDays[this.selectedMonth];
    const firstDay = new Date(this.selectedYear, this.selectedMonth, 1);
    let jsDay = firstDay.getDay();
    let firstDayOfWeek = jsDay === 0 ? 6 : jsDay - 1;
    // Find the week number to show: if today is in this month/year, show its week, else show first week
    const today = new Date();
    let weekStartDate = 1;
    if (today.getFullYear() === this.selectedYear && today.getMonth() === this.selectedMonth) {
      const todayDate = today.getDate();
      const todayCell = todayDate + firstDayOfWeek - 1;
      const weekNum = Math.floor(todayCell / 7);
      weekStartDate = weekNum * 7 - firstDayOfWeek + 2;
    }
    const date = weekStartDate + day;
    if (date > 0 && date <= daysInMonth) {
      return date;
    }
    return '';
  }

  getDayViewLabel(): string {
    return `${this.months[this.selectedDayDate.getMonth()]} ${this.selectedDayDate.getDate()}, ${this.selectedDayDate.getFullYear()}`;
  }

  getWeekViewLabel(): string {
    const weekEnd = new Date(this.selectedWeekStartDate);
    weekEnd.setDate(weekEnd.getDate() + 6);
    
    const startMonth = this.months[this.selectedWeekStartDate.getMonth()];
    const endMonth = this.months[weekEnd.getMonth()];
    const startDay = this.selectedWeekStartDate.getDate();
    const endDay = weekEnd.getDate();
    const year = this.selectedWeekStartDate.getFullYear();
    
    if (this.selectedWeekStartDate.getMonth() === weekEnd.getMonth()) {
      return `${startMonth} ${startDay}-${endDay}, ${year}`;
    } else {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
    }
  }

  // Get events for a specific date (for daily/weekly views)
  getEventsForDate(date: Date) {   
    const matchingEvents = this.allEvents.filter(ev => {
      if (!ev.start_date || !ev.end_date) return false;
      const matchDate = this.getBeforeDuringOrAfter(date, ev.start_date, ev.end_date);
      
      // Always show holiday events regardless of user permissions
      if (ev.source === 'holiday' || ev.isHoliday) {
        return matchDate;
      }
      
      // Role-based filtering logic for regular events
      let matchCreator = false;
      if (this.currentUser && (this.currentUser.isAdmin || this.currentUser.isViewer)) {
        // Admins can see all events, or filter by selected requestor
        const assignee = String(ev.assigned_to || '').trim().toLowerCase() || 'unassigned';
        matchCreator = !this.currentUser.isAdmin || this.allAssigneesSelected || this.selectedAssignees.includes(assignee);
      } else if (this.currentUser) {
        // Regular users can only see their own events
        const userGmid = this.currentUser.gmid?.toLowerCase();
        const userEmail = this.currentUser.userPrincipalName || this.currentUser.mail;
        const userMail = this.currentUser.mail?.toLowerCase(); // Direct mail field
        const requestorEmail = this.getPersonEmail(ev.requestor);
        const requestorGmid = this.getPersonGmid(ev.requestor);
        const eventLeaderEmail = this.getPersonEmail(ev.event_leader);
        const eventLeaderGmid = this.getPersonGmid(ev.event_leader);
        
        // Enhanced matching with email formats
        matchCreator = userGmid && (
          ev.creator === userGmid ||
          // Check original fields in case of format differences
          requestorGmid === userGmid ||
          eventLeaderGmid === userGmid ||
          // Email matching
          (userEmail && ev.creator === userEmail.toLowerCase()) ||
          (userEmail && requestorEmail === userEmail.toLowerCase()) ||
          (userEmail && eventLeaderEmail === userEmail.toLowerCase()) ||
          // Direct mail field match - THIS IS THE KEY FIX
          (userMail && ev.creator === userMail) ||
          (userMail && requestorEmail === userMail) ||
          (userMail && eventLeaderEmail === userMail) ||
          // Partial matching for email formats like hz71z9@gm.com
          (userEmail && ev.creator && ev.creator.includes(userGmid)) ||
          (userEmail && requestorEmail.includes(userGmid)) ||
          (userEmail && eventLeaderEmail.includes(userGmid)) ||
          requestorGmid.includes(userGmid) ||
          eventLeaderGmid.includes(userGmid)
        );        
      }
      
      return matchDate && matchCreator;
    });
    
    // Sort events so holidays appear first
    return matchingEvents.sort((a, b) => {
      const aIsHoliday = a.source === 'holiday' || a.isHoliday;
      const bIsHoliday = b.source === 'holiday' || b.isHoliday;
      
      // If one is holiday and one is not, holiday comes first
      if (aIsHoliday && !bIsHoliday) return -1;
      if (!aIsHoliday && bIsHoliday) return 1;
      
      // If both are holidays or both are regular events, sort by start time
      if (a.start_date && b.start_date) {
        return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
      }
      
      return 0; // Keep original order if no dates available
    });
  }

  // Get time slots for daily view (30-minute intervals) - 24 hours
  getTimeSlots() {
    // Generate slots labeled explicitly in Eastern Time so labels align with layout calculations
    const slots: { hour: number; minutes: number; label: string }[] = [];
    const base = new Date(this.selectedDayDate);
    base.setHours(0,0,0,0);
    for (let i = 0; i < 48; i++) {
      const minutesFromMidnight = i * 30;
      const hour = Math.floor(minutesFromMidnight / 60);
      const minutes = minutesFromMidnight % 60;
      // 12h label without converting time zone
      const h12 = hour % 12 === 0 ? 12 : hour % 12;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const label = `${h12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
      slots.push({ hour, minutes, label });
    }
    return slots;
  }

  // Get date for a specific day in the current week view
  // dayIndex: 0=Sunday, 1=Monday, 2=Tuesday, ..., 6=Saturday
  getWeekDayDate(dayIndex: number): Date {
    const date = new Date(this.selectedWeekStartDate);
    date.setDate(date.getDate() + dayIndex);
    return date;
  }

  // Get formatted date string for week view
  getWeekDayDateString(dayIndex: number): string {
    const date = this.getWeekDayDate(dayIndex);
    return date.getDate().toString();
  }

  // Check if a week day is today
  isWeekDayToday(dayIndex: number): boolean {
    const date = this.getWeekDayDate(dayIndex);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  // Check if selected day is today
  isDayViewToday(): boolean {
    const today = new Date();
    return this.selectedDayDate.toDateString() === today.toDateString();
  }

 

  // Helper: Get number of days in month
  getDaysInMonth(year: number, month: number): number {
    return new Date(year, month + 1, 0).getDate();
  }

  // Helper: Get the weekday (0=Mon, 6=Sun) of the first day of the month
  getFirstDayOfWeek(year: number, month: number): number {
    // JS: 0=Sun, 1=Mon,..., so shift to 0=Mon
    // let day = new Date(year, month, 1).getDay();
    return new Date(year, month, 1).getDay();//day === 0 ? 6 : day - 1;
  }

  // Used in template to get the date for a cell (returns '' if not in current month)
  getDateForCell(week: number, day: number): string {
    const firstDay = this.getFirstDayOfWeek(this.selectedYear, this.selectedMonth);
    const daysInMonth = this.getDaysInMonth(this.selectedYear, this.selectedMonth);
    const cellNum = week * 7 + day;
    const dateNum = cellNum - firstDay + 1;
    if (dateNum < 1 || dateNum > daysInMonth) return '';
    return dateNum.toString();
  }

  // Used in template to get ISO date string for a cell (YYYY-MM-DD)
  private getCellDate(week: number, day: number): Date | null {
    const dateStr = this.getDateForCell(week, day);
    if (!dateStr) {
      return null;
    }

    const dayNum = parseInt(dateStr, 10);
    if (isNaN(dayNum)) {
      return null;
    }

    const date = new Date(this.selectedYear, this.selectedMonth, dayNum);
    date.setHours(12, 0, 0, 0); // normalise to local midday to avoid timezone drift
    return date;
  }

  getISODateForCell(week: number, day: number): string | null {
    const date = this.getCellDate(week, day);
    if (!date) {
      return null;
    }

    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const dayStr = date.getDate().toString().padStart(2, '0');
    return `${date.getFullYear()}-${month}-${dayStr}`;
  }

  /**
   * True when the event covers the whole day of dateIn, allowing a 1 hr tolerance at each
   * end (e.g. 12:30 AM - 11:30 PM still counts as covering the day).
   */
  isDayBetweenDates(dateIn: any, startDate: any, endDate: any): boolean {
    if (!startDate || !endDate || !dateIn) {
      return false;
    }
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const dayStart = new Date(dateIn);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    const toleranceMs = 60 * 60 * 1000;

    return start <= dayStart.getTime() + toleranceMs && end >= dayEnd.getTime() - toleranceMs;
  }
  
  /**
   * Checks to see if dateIn is on the same day as startDate, between startDate and endDate, or on the same day as endDate
   * Does all the checking in eastern time
   * @param dateIn 
   * @param startDate 
   * @param endDate 
   * @returns boolean - true if dateIn is between startDate and endDate (inclusive)
   */
  getBeforeDuringOrAfter(dateIn: any, startDate: any, endDate: any): boolean {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const checkDate = new Date(dateIn);
    // Set all to noon to avoid DST issues
    start.setHours(12, 0, 0, 0);
    end.setHours(12, 0, 0, 0);
    checkDate.setHours(12, 0, 0, 0);
    return checkDate >= start && checkDate <= end;
  }

  // Get events for a given day, filtered by creator
  getEventsForCell(week: number, day: number) {
    const cellDate = this.getCellDate(week, day);
    if (!cellDate) {
      return [];
    }

    return this.getEventsForDate(cellDate);
  }

  // Returns the number of additional events beyond the visible threshold for a month cell
  getVisibleEventsForCell(week: number, day: number) {
    return this.getEventsForCell(week, day).slice(0, this.monthCellVisibleLimit);
  }

  getAdditionalEventsCount(week: number, day: number, visibleLimit: number = this.monthCellVisibleLimit): number {
    const events = this.getEventsForCell(week, day);
    return events.length > visibleLimit ? (events.length - visibleLimit) : 0;
  }

  // Total events (used for badge display)
  getEventCountForCell(week: number, day: number): number {
    return this.getEventsForCell(week, day).length;
  }

  // Return display text for badge (cap at 99+ to preserve circle shape)
  getEventCountBadge(week: number, day: number): string {
    const hiddenCount = this.getAdditionalEventsCount(week, day, this.monthCellVisibleLimit);
    if (hiddenCount <= 0) {
      return '0';
    }
    const capped = hiddenCount > 99 ? 99 : hiddenCount;
    return capped.toString();
  }
  // Navigate directly to daily view for a given month cell (badge click)
  goToDayFromCell(week: number, day: number, ev?: Event) {
    if (ev) {
      ev.stopPropagation();
      ev.preventDefault();
    }
    const dateStr = this.getDateForCell(week, day);
    if (!dateStr) return;
    const dayNum = parseInt(dateStr, 10);
    this.selectedDayDate = new Date(this.selectedYear, this.selectedMonth, dayNum);
    this.syncMonthYearWithSelectedDay();
    this.calendarView = { day: true, week: false, month: false };
    this.calendarViewSelected = 'day';
    
    // Update URL to maintain browser history
    this.updateUrlState();
  }

  // Get remaining (overflow) events for a cell beyond visible limit (default 3)
  getRemainingEventsForCell(week: number, day: number, visibleLimit: number = 3) {
    const events = this.getEventsForCell(week, day);
    if (events.length <= visibleLimit) return [];
    return events.slice(visibleLimit);
  }

  // Check if a calendar cell represents today
  isToday(week: number, day: number): boolean {
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    // Only highlight if we're viewing the current month and year
    if (this.selectedMonth !== currentMonth || this.selectedYear !== currentYear) {
      return false;
    }

    const cellDate = this.getDateForCell(week, day);
    return cellDate === currentDay.toString();
  }

  // Get CSS classes for event styling based on source and creator
  getEventClasses(event: any): string {
    let classes = '';
    
    if (event.source === 'database') {
      classes += 'database-event ';
    } else {
      classes += 'graph-event ';
    }
    
    // Add creator-specific styling
    if (event.creator) {
      classes += event.creator + '-event';
    }
    
    return classes.trim();
  }

  // Get event style object for dynamic coloring
  getEventStyle(event: any): any {
    // Special styling for holiday events
    if (event.source === 'holiday' || event.isHoliday) {
      return {
        'background-color': '#dc2626', // Red background for holidays
        'color': 'white',
        'border-left': '3px solid #b91c1c', // Darker red border
        'border': '1px solid #991b1b',
        'font-weight': '600',
        'text-shadow': '0 1px 2px rgba(0, 0, 0, 0.4)',
        'opacity': '0.9'
      };
    }
    
    const assigneeEmail = String(event?.assigned_to || '').toLowerCase();
    let assigneeGMID = null;
    // get gmid from all admins with this email
    assigneeGMID = this.allAdmins.find(admin => String(admin?.email || '').toLowerCase() === assigneeEmail)?.gmid?.toLowerCase() || null;
    
    if (assigneeGMID && this.requestorColors[assigneeGMID]) {
      
      const baseColor = this.requestorColors[assigneeGMID];
      const darkerColor = this.darkenColor(baseColor, 20);
      
      const style = {
        'background-color': baseColor,
        'color': 'white',
        'border-left': `3px solid ${darkerColor}`,
        'border': `1px solid ${this.darkenColor(baseColor, 35)}`,
        'font-weight': '500',
        'text-shadow': '0 1px 2px rgba(0, 0, 0, 0.3)'
      };
      
      return style;
    }
    
    // Default styling for graph events or events without colors
    const defaultStyle = {
      'background-color': '#f3f4f6',
      'color': '#374151',
      'border-left': '3px solid #d1d5db',
      'border': '1px solid #d0d5da'
    };
   
    return defaultStyle;
  }

  // Helper method to format event time for display
  formatEventTime(event: any): string {
    if (!event.start_date) return '';
    
    // Show "All Day" for holiday events
    if (event.source === 'holiday' || event.isHoliday) {
      return 'All Day';
    }
    
    try {
      const startDate = new Date(event.start_date);
      const endDate = event.end_date ? new Date(event.end_date) : null;
      
      // fmtDate just takes the date part and formats as Mon D. NO TIME ZONE CONVERSION, just format what is provided
      const fmtDate = (date: Date): string => {
        const month = date.getMonth();
        // convert month number to short name
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const day = date.getDate();
        return `${monthNames[month]} ${day}`;
      }

      const to12Hour = (timeStr: string): string => {
        const [hourStr, minuteStr] = timeStr.split(':');
        let hour = parseInt(hourStr, 10);
        const minute = parseInt(minuteStr, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        hour = hour % 12;
        if (hour === 0) hour = 12;
        return `${hour}:${minute.toString().padStart(2, '0')} ${ampm}`;
      }

      // Do not convert time zones, just display time provided. 12 hour time, not 24
      let startTime = to12Hour(startDate.toTimeString().slice(0,5));
      let endTime = endDate ? to12Hour(endDate.toTimeString().slice(0,5)) : '';

      if (endDate) {
        if (!(this.easternDateKey(startDate) === this.easternDateKey(endDate))) {
          return `${fmtDate(startDate)} ${startTime} - ${fmtDate(endDate)} ${endTime}`;
        }
        return `${startTime} - ${endTime}`;
      }
      return startTime;
    } catch (error) {
      console.error('Error formatting event time:', error);
      return '';
    }
  }

  /**
   * Compute layout for daily events so they stack vertically and only overlap horizontally when times intersect.
   * Creates clusters of overlapping events, assigns columns for each cluster, and returns style objects
   * with absolute positioning relative to the day timeline.
   */
  getDayEventLayouts(): { event: any; style: any; startMin?: number; endMin?: number; column?: number; totalCols?: number }[] {
    const dayEvents = this.getEventsForDate(this.selectedDayDate);
    if (!dayEvents || dayEvents.length === 0) return [];

    // Eastern day key for the selected date (ensures consistency between labeling & layout)
    const selectedEasternKey = this.easternDateKey(this.selectedDayDate);

    // Exclude all-day events (rendered separately) and force any cross-midnight (in Eastern) into all-day bar
    const timedEvents = dayEvents.filter(ev => {
      if (this.isAllDayLike(ev, this.selectedDayDate)) return false; // already an all-day style event
      return true;
    });
    if (timedEvents.length === 0) return [];

    console.log('All events being laid out for day view:', timedEvents);

    this.measureSlotHeightIfNeeded();
    const slotHeight = this._slotHeight; // dynamically measured px per 30-minute slot

    // Some timed events will have start/end dates that are outside the selected day (multi-day events) but still need to show the correct amount of time it happens on this day
    const normalized = timedEvents.map(ev => {
      // If event starts a different day than today but ends today
      if (this.easternDateKey(new Date(ev.end_date)) === selectedEasternKey &&
          this.easternDateKey(new Date(ev.start_date)) !== selectedEasternKey) {
        // Normalize start to midnight today
        const normalizedStart = new Date(this.selectedDayDate);
        normalizedStart.setHours(0, 0, 0, 0);
        const endC = this.coerceEventDate(ev.end_date);
        let endMin = endC.valid ? (endC.minutes as number) : 1440;
        if (endMin > 1440) endMin = 1440;
        const normalizedEnd = new Date(this.selectedDayDate);
        normalizedEnd.setHours(Math.floor(endMin/60), endMin % 60, 0, 0);
        return { event: ev, start: normalizedStart, end: normalizedEnd, startMin: 0, endMin };
      }
      // If event ends a different day than today but starts today
      if (this.easternDateKey(new Date(ev.start_date)) === selectedEasternKey &&
          this.easternDateKey(new Date(ev.end_date)) !== selectedEasternKey) {
        // Normalize end to 11:59 PM today
        const startC = this.coerceEventDate(ev.start_date);
        let startMin = startC.valid ? (startC.minutes as number) : 0;
        if (startMin < 0) startMin = 0;
        const normalizedStart = new Date(this.selectedDayDate);
        normalizedStart.setHours(Math.floor(startMin/60), startMin % 60, 0, 0);
        const normalizedEnd = new Date(this.selectedDayDate);
        normalizedEnd.setHours(23, 59, 0, 0);
        return { event: ev, start: normalizedStart, end: normalizedEnd, startMin, endMin: 1439 };
      }

      const startC = this.coerceEventDate(ev.start_date);
      const endC = this.coerceEventDate(ev.end_date || ev.start_date);
      if (!startC.valid || !endC.valid || startC.dateKey !== selectedEasternKey || endC.dateKey !== selectedEasternKey) return null;
      let startMin = startC.minutes as number;
      let endMin = endC.minutes as number;
      // Ensure strictly positive duration so simultaneous events overlap correctly
      if (endMin <= startMin) endMin = startMin + 1;
      if (endMin > 1440) endMin = 1440;
      if (startMin < 0) startMin = 0;
      if (startMin > 1440) startMin = 1440;
      const normalizedStart = new Date(this.selectedDayDate);
      normalizedStart.setHours(Math.floor(startMin/60), startMin % 60, 0, 0);
      const normalizedEnd = new Date(this.selectedDayDate);
      normalizedEnd.setHours(Math.floor(endMin/60), endMin % 60, 0, 0);
      return { event: ev, start: normalizedStart, end: normalizedEnd, startMin, endMin };
    })
    .filter((v): v is { event:any; start:Date; end:Date; startMin:number; endMin:number } => !!v)
    .sort((a,b) => a.startMin - b.startMin || (a.endMin - b.endMin));

    // Tolerance snapping (≤2 minutes considered simultaneous)
    const SNAP_TOLERANCE = 2;
    for (let i = 1; i < normalized.length; i++) {
      const prev = normalized[i-1];
      const cur = normalized[i];
      const delta = cur.startMin - prev.startMin;
      if (delta > 0 && delta <= SNAP_TOLERANCE) {
        cur.startMin = prev.startMin;
        if (cur.endMin <= cur.startMin) cur.endMin = cur.startMin + 1;
      }
    }
    normalized.sort((a,b) => a.startMin - b.startMin || (a.endMin - b.endMin));

    // Build clusters of overlapping intervals

    // Sweep-line based concurrency layout (classic calendar algorithm)
    interface LEvent { event:any; startMin:number; endMin:number; col?:number; cols?:number; heightPx:number; topPx:number; clusterId:number; }
    const clusters: LEvent[][] = [];
    let activeCluster: LEvent[] = [];
    let clusterMaxEnd = -1;
    normalized.forEach(rec => {
      if (activeCluster.length === 0) {
        activeCluster.push({ event:rec.event, startMin:rec.startMin, endMin:rec.endMin, heightPx:0, topPx:0, clusterId:clusters.length });
        clusterMaxEnd = rec.endMin;
      } else if (rec.startMin < clusterMaxEnd) { // overlap with current cluster
        activeCluster.push({ event:rec.event, startMin:rec.startMin, endMin:rec.endMin, heightPx:0, topPx:0, clusterId:clusters.length });
        clusterMaxEnd = Math.max(clusterMaxEnd, rec.endMin);
      } else { // new cluster
        clusters.push(activeCluster);
        activeCluster = [{ event:rec.event, startMin:rec.startMin, endMin:rec.endMin, heightPx:0, topPx:0, clusterId:clusters.length }];
        clusterMaxEnd = rec.endMin;
      }
    });
    if (activeCluster.length) clusters.push(activeCluster);

    const layouts: { event:any; style:any; startMin:number; endMin:number; column:number; totalCols:number }[] = [];

    clusters.forEach((cluster, idx) => {
      // Assign columns greedily
      const columns: LEvent[][] = [];
      cluster.forEach(ev => {
        let placed = false;
        for (let ci=0; ci<columns.length; ci++) {
          const last = columns[ci][columns[ci].length-1];
            if (last.endMin <= ev.startMin) { // can reuse column when strictly finished before start
            columns[ci].push(ev); ev.col = ci; placed = true; break;
          }
        }
        if (!placed) { ev.col = columns.length; columns.push([ev]); }
      });
      const totalCols = columns.length;
      // Precompute per-event top/height
      cluster.forEach(ev => {
        const top = (ev.startMin / 30) * slotHeight;
        let h = ((ev.endMin - ev.startMin) / 30) * slotHeight;
        if (h < 24) h = 24;
        const maxH = 48 * slotHeight - top; if (h>maxH) h = maxH;
        ev.topPx = top; ev.heightPx = h;
      });
      // For each event compute dynamic concurrency (columns that actually overlap it)
      cluster.forEach(ev => {
        const overlapping = cluster.filter(o => o.startMin < ev.endMin && o.endMin > ev.startMin);
        const distinctCols = Array.from(new Set(overlapping.map(o => o.col!))).sort((a,b)=>a-b);
        const compressedIndex = distinctCols.indexOf(ev.col!);
        const concurrency = distinctCols.length;
        const widthPct = 100 / concurrency;
        const leftPct = compressedIndex * widthPct;
        const baseStyle = this.getEventStyle(ev.event);
        const style:any = {
          ...baseStyle,
          position:'absolute',
          top: `${ev.topPx}px`,
          // raw left/width also set via CSS variables for !important override in SCSS
          left: `${leftPct}%`,
          width: `calc(${widthPct}% - 2px)`,
          height: `${ev.heightPx}px`,
          'box-sizing':'border-box',
          padding:'4px 4px 4px 6px',
          overflow:'hidden',
          'border-radius':'4px',
          'z-index': 10 + (compressedIndex || 0),
          display:'flex',
          'flex-direction':'column',
          'justify-content':'flex-start',
          gap:'2px',
          'pointer-events':'auto',
          'min-width': `calc(${widthPct}% - 2px)`,
          'max-width': `calc(${widthPct}% - 2px)`,
          '--cal-left': `${leftPct}%`,
          '--cal-width': `${widthPct}%`
        };
        layouts.push({ event: ev.event, style, startMin: ev.startMin, endMin: ev.endMin, column: ev.col!, totalCols });
      });
    });

    return layouts;
  }

  // Debug code purged for production

  /** Unified normalization for raw timestamp strings into Eastern day key + minutes. */
  private coerceEventDate(raw: any): { valid: boolean; dateKey: string | null; minutes: number | null } {
    if (!raw) return { valid: false, dateKey: null, minutes: null };
    if (typeof raw !== 'string') {
      try {
        const d = new Date(raw);
        if (isNaN(d.getTime())) return { valid: false, dateKey: null, minutes: null };
        return { valid: true, dateKey: this.easternDateKey(d), minutes: this.easternMinutesFrom(d) };
      } catch { return { valid: false, dateKey: null, minutes: null }; }
    }
    const value = raw.trim();
    // Naive: YYYY-MM-DD HH:MM[:SS]
    const naive = value.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (naive) {
      const datePart = naive[1];
      const h = parseInt(naive[2], 10); const m = parseInt(naive[3], 10);
      if (isNaN(h) || isNaN(m) || h<0 || h>23 || m<0 || m>59) return { valid: false, dateKey: null, minutes: null };
      return { valid: true, dateKey: datePart, minutes: h*60 + m };
    }
    // ISO / offset attempt
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return { valid: false, dateKey: null, minutes: null };
      return { valid: true, dateKey: this.easternDateKey(d), minutes: this.easternMinutesFrom(d) };
    } catch { return { valid: false, dateKey: null, minutes: null }; }
  }

  // (former toggle & listing helpers removed)

  // Dynamic half-hour slot height (pixels) with measurement cache
  private _slotHeight: number = 30;
  private _measuredDayKey: string | null = null;

  private measureSlotHeightIfNeeded(): void {
    try {
      const key = this.easternDateKey(this.selectedDayDate);
      if (this._measuredDayKey === key) return;
      const el = document.querySelector('.day-schedule .hour-slot');
      if (el) {
        const rect = (el as HTMLElement).getBoundingClientRect();
        if (rect.height && Math.abs(rect.height - this._slotHeight) > 0.5) {
          this._slotHeight = rect.height;
        }
        this._measuredDayKey = key;
      }
    } catch { /* silent */ }
  }

  getDayLayerHeight(): number { return 48 * this._slotHeight; }

  // Diagnostics removed

  /** Minutes since midnight Eastern Time */
  private easternMinutesFrom(d: Date): number {
    return d.getHours() * 60 + d.getMinutes();
  }

  /** YYYY-MM-DD Eastern key */
  private easternDateKey(d: Date): string {
    const y = d.getFullYear();
    const m = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // Determine if an event should be treated as an all-day / multi-day banner (e.g., vacation)
  private isAllDayLike(ev: any, dateIn: any): boolean {
    if (!ev?.start_date || !ev?.end_date) return false;
    
    // Holiday events are always treated as all-day events
    if (ev.source === 'holiday' || ev.isHoliday) {
      return true;
    }
    
    // If start date is before dateIn or end date is after dateIn, it's all-day for that date
    if (dateIn && this.isDayBetweenDates(dateIn, ev.start_date, ev.end_date)) {
      return true;
    }
    return false;
  }

  getAllDayEventsForSelectedDay(): any[] {
    const events = this.getEventsForDate(this.selectedDayDate) || [];
    const allDayEvents = events.filter(e => this.isAllDayLike(e, this.selectedDayDate));
    
    // Sort all-day events so holidays appear first
    return allDayEvents.sort((a, b) => {
      const aIsHoliday = a.source === 'holiday' || a.isHoliday;
      const bIsHoliday = b.source === 'holiday' || b.isHoliday;
      
      // If one is holiday and one is not, holiday comes first
      if (aIsHoliday && !bIsHoliday) return -1;
      if (!aIsHoliday && bIsHoliday) return 1;
      
      return 0; // Keep original order if both are holidays or both are regular all-day events
    });
  }

  getAllDayEventStyle(ev: any): any {
    const base = this.getEventStyle(ev);
    return {
      ...base,
      'flex': '0 0 auto',
      'min-width': '140px',
      'font-weight': '500'
    };
  }

  // Helper method to get event duration in hours for display
  getEventDuration(event: any): number {
    if (!event.start_date || !event.end_date) return 1; // Default to 1 hour
    
    try {
      const startDate = new Date(event.start_date);
      const endDate = new Date(event.end_date);
      const durationMs = endDate.getTime() - startDate.getTime();
      const durationHours = durationMs / (1000 * 60 * 60);
      return Math.max(0.5, Math.min(durationHours, 8)); // Between 30 minutes and 8 hours
    } catch (error) {
      console.error('Error calculating event duration:', error);
      return 1;
    }
  }

  // Enhanced method to get event tooltip with proper time information
  getEventTooltip(event: any): string {
    const title = event.title || event.subject || 'Untitled Event';
    const time = this.formatEventTime(event);
    const requestor = this.getPersonDisplay(event.requestor) || this.getPersonDisplay(event.event_leader) || 'Unknown';
    const location = event.primary_presenting_location || '';
    
    let tooltip = `${title}`;
    if (time) tooltip += `\nTime: ${time}`;
    if (requestor) tooltip += `\nRequestor: ${requestor}`;
    if (location) tooltip += `\nLocation: ${location}`;
    
    return tooltip;
  }

  // Helper method to darken a hex color
  darkenColor(color: string, percent: number): string {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  }

  // Handle requestor filter changes, including "Add New Requestor" option
  onAssigneeFilterChange(event: any): void {
    // Only allow admin users to change the filter
    if (!this.currentUser.isAdmin) {
      console.warn('Non-admin users cannot change requestor filter');
      return;
    }
    
    const values: string[] = event.value;
    const selectAllChecked = values.includes('__all__');
    this.selectedAssignees = selectAllChecked !== this.allAssigneesSelected
      ? (selectAllChecked ? this.assigneeOptions : [])
      : values.filter(value => value !== '__all__');
    this.allAssigneesSelected = this.assigneeOptions.every(value => this.selectedAssignees.includes(value));
    if (this.assigneeStorageKey) {
      try {
        localStorage.setItem(this.assigneeStorageKey, JSON.stringify({
          all: this.allAssigneesSelected,
          selected: this.selectedAssignees
        }));
      } catch {}
    }
  }

  // Handle event clicks to navigate to event form page
  onEventClick(event: any, clickEvent?: Event): void {
    // Prevent event bubbling if clickEvent is provided
    if (clickEvent) {
      clickEvent.stopPropagation();
      clickEvent.preventDefault();
    }
    
    // Don't allow clicking on holiday events
    if (event && (event.source === 'holiday' || event.isHoliday)) {
      this.snackBar.open('Holiday events cannot be edited.', 'Close', {
        duration: 2000,
        panelClass: ['info-snackbar']
      });
      return;
    }
    
    // Only handle database events (not Graph API events) since they have event_id
    if (event && event.source === 'database' && event.event_id) {
      
      // Show loading message
      this.snackBar.open('Opening event for editing...', '', {
        duration: 1500,
        panelClass: ['success-snackbar']
      });
      
      // Build the hash-based URL for the event request page and open in a new tab
      const urlTree = this.router.createUrlTree(['/request'], {
        queryParams: { id: event.event_id }
      });
      const internalUrl = this.router.serializeUrl(urlTree); // e.g. "/request?id=123"

      // Because the app uses useHash routing, construct a full URL that
      // preserves the current origin/path and puts the route in the hash.
      const base = window.location.origin + window.location.pathname; // points to index.html
      const fullUrl = `${base}#${internalUrl.startsWith('/') ? internalUrl : '/' + internalUrl}`;

      window.open(fullUrl, '_blank');
    } else {
      // console.log('Event cannot be edited. Source:', event?.source, 'ID:', event?.event_id);
      // For Graph events or events without ID, show a message
      this.snackBar.open('This event cannot be edited.', 'Close', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
    }
  }

  // Load custom colors from database for all admins/users
  loadCustomColors(): void {
    this.allAdmins.forEach((admin: any) => {
      const adminGmid = admin.gmid?.toLowerCase();
      // const rawColor = admin.preferredcolor;
      if (adminGmid) {
        this.registerColorAliases(adminGmid, admin.preferredcolor);
        // console.log(`Loaded custom color (with aliases) for ${adminGmid}: ${admin.preferredcolor}`);
      }
    });

    
    // Trigger change detection to update the UI
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    if (this.colorUpdateSubscription) {
      this.colorUpdateSubscription.unsubscribe();
    }
  }
}